import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarPlus, ChevronDown, ChevronUp, History, Pencil, X } from 'lucide-react';

import { ApiError, api } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

interface AbsenceBalance {
  absenceTypeId: string;
  code: string;
  name: string;
  color: string;
  consumesBalance: boolean;
  availableDays: number;
}

type MovementType =
  | 'ANNUAL_GRANT'
  | 'MANUAL_ADJUSTMENT'
  | 'CONSUMED'
  | 'REVERTED'
  | 'MIGRATION_INITIAL'
  | 'EXPIRED'
  | 'OVERTIME_CONVERSION';

interface Movement {
  id: string;
  movementType: MovementType;
  days: number;
  effectiveDate: string;
  notes: string | null;
  createdAt: string;
  createdByUser: { id: string; fullName: string; email: string } | null;
  absenceRequest: {
    id: string;
    startDate: string;
    endDate: string;
    status: string;
  } | null;
}

interface MovementsResponse {
  type: { id: string; code: string; name: string };
  items: Movement[];
}

const MOVEMENT_LABEL: Record<MovementType, string> = {
  ANNUAL_GRANT: 'Concesión anual',
  MANUAL_ADJUSTMENT: 'Ajuste manual',
  CONSUMED: 'Consumo',
  REVERTED: 'Reversión',
  MIGRATION_INITIAL: 'Saldo inicial',
  EXPIRED: 'Caducidad',
  OVERTIME_CONVERSION: 'Conversión de horas a compensar',
};

const MOVEMENT_TONE: Record<MovementType, 'green' | 'blue' | 'neutral' | 'amber' | 'red'> = {
  ANNUAL_GRANT: 'green',
  MANUAL_ADJUSTMENT: 'blue',
  CONSUMED: 'neutral',
  REVERTED: 'amber',
  MIGRATION_INITIAL: 'neutral',
  EXPIRED: 'red',
  OVERTIME_CONVERSION: 'blue',
};

export function AbsenceBalancesPanel({ userId }: { userId: string }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<AbsenceBalance | null>(null);
  const [registering, setRegistering] = useState(false);
  const [expandedTypeId, setExpandedTypeId] = useState<string | null>(null);

  const balances = useQuery({
    queryKey: ['admin', 'users', userId, 'absence-balances'],
    queryFn: () => api<AbsenceBalance[]>(`/admin/users/${userId}/absence-balances`),
    enabled: Boolean(userId),
  });

  const list = balances.data ?? [];

  return (
    <section className="bg-card border border-border rounded-sm shadow-sm overflow-hidden">
      <header className="flex items-start justify-between gap-4 px-7 py-4 border-b border-border">
        <div>
          <h2 className="text-base font-semibold tracking-tightish">Saldo de vacaciones</h2>
          <p className="text-[12px] text-muted-foreground mt-0.5 leading-relaxed">
            Días disponibles por tipo. "Ajustar" añade/quita días por motivos puntuales
            (compensaciones, errores). El histórico muestra cada movimiento del ledger
            (append-only).
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="flex-shrink-0"
          onClick={() => setRegistering(true)}
        >
          <CalendarPlus className="h-3.5 w-3.5" />
          Registrar ausencia / viaje
        </Button>
      </header>

      {balances.isLoading ? (
        <p className="px-7 py-6 text-sm text-muted-foreground">Cargando…</p>
      ) : balances.isError ? (
        <p role="alert" className="px-7 py-6 text-sm text-destructive">
          {balances.error instanceof ApiError ? balances.error.message : 'Error al cargar saldos'}
        </p>
      ) : list.length === 0 ? (
        <p className="px-7 py-6 text-sm text-muted-foreground">
          No hay tipos de ausencia con saldo configurados.
        </p>
      ) : (
        <ul>
          {list.map((b) => {
            const isOpen = expandedTypeId === b.absenceTypeId;
            return (
              <li key={b.absenceTypeId} className="border-b border-border last:border-b-0">
                <div className="flex items-center gap-3 px-7 py-4">
                  <span
                    className="inline-block h-2.5 w-2.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: b.color }}
                    aria-hidden
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-[14px] font-semibold text-foreground leading-tight">
                      {b.name}
                    </p>
                    <p className="font-mono text-[11px] text-muted-foreground leading-tight mt-0.5">
                      {b.code}
                    </p>
                  </div>
                  <p
                    className={cn(
                      'font-mono text-[18px] font-medium tabular-nums tracking-tight2',
                      b.availableDays < 0 ? 'text-destructive' : 'text-foreground',
                    )}
                  >
                    {formatDays(b.availableDays)}
                  </p>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      setExpandedTypeId((curr) =>
                        curr === b.absenceTypeId ? null : b.absenceTypeId,
                      )
                    }
                    aria-label={`Ver histórico de ${b.name}`}
                    aria-expanded={isOpen}
                  >
                    <History className="h-3.5 w-3.5" />
                    Histórico
                    {isOpen ? (
                      <ChevronUp className="h-3.5 w-3.5" />
                    ) : (
                      <ChevronDown className="h-3.5 w-3.5" />
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEditing(b)}
                    aria-label={`Ajustar saldo de ${b.name}`}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                    Ajustar
                  </Button>
                </div>
                {isOpen ? (
                  <MovementsHistory userId={userId} absenceTypeId={b.absenceTypeId} />
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      {editing ? (
        <AdjustModal
          userId={userId}
          balance={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            queryClient.invalidateQueries({
              queryKey: ['admin', 'users', userId, 'absence-balances'],
            });
          }}
        />
      ) : null}

      {registering ? (
        <RegisterAbsenceModal
          userId={userId}
          onClose={() => setRegistering(false)}
          onSaved={() => {
            setRegistering(false);
            queryClient.invalidateQueries({
              queryKey: ['admin', 'users', userId, 'absence-balances'],
            });
          }}
        />
      ) : null}
    </section>
  );
}

interface AbsenceTypeOption {
  id: string;
  code: string;
  name: string;
  consumesBalance: boolean;
}

/**
 * RRHH registra una ausencia/viaje EN NOMBRE del empleado. Nace ya
 * aprobada (POST /admin/users/:id/absences). No valida saldo: el sistema
 * permite saldo negativo (deuda) y el viaje no consume.
 */
function RegisterAbsenceModal({
  userId,
  onClose,
  onSaved,
}: {
  userId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [absenceTypeId, setAbsenceTypeId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isHalfDay, setIsHalfDay] = useState(false);
  const [halfDayPart, setHalfDayPart] = useState<'AM' | 'PM'>('AM');
  const [comment, setComment] = useState('');

  const types = useQuery({
    queryKey: ['absences', 'types'],
    queryFn: () => api<AbsenceTypeOption[]>('/absences/types'),
  });

  const effectiveEnd = isHalfDay ? startDate : endDate;

  const mutation = useMutation({
    mutationFn: () =>
      api<unknown>(`/admin/users/${userId}/absences`, {
        method: 'POST',
        body: JSON.stringify({
          absenceTypeId,
          startDate,
          endDate: effectiveEnd,
          isHalfDay,
          ...(isHalfDay ? { halfDayPart } : {}),
          comment: comment.trim() || undefined,
        }),
      }),
    onSuccess: onSaved,
  });

  const canSubmit =
    absenceTypeId !== '' &&
    startDate !== '' &&
    effectiveEnd !== '' &&
    effectiveEnd >= startDate &&
    !mutation.isPending;

  const error = mutation.error
    ? mutation.error instanceof ApiError
      ? mutation.error.message
      : 'Error al registrar la ausencia'
    : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      <div
        aria-hidden
        onClick={onClose}
        className="absolute inset-0"
        style={{ background: 'rgba(10,14,31,0.4)', backdropFilter: 'blur(2px)' }}
      />
      <div className="relative w-full max-w-[480px] bg-card rounded-sm shadow-lg overflow-hidden">
        <header className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="text-[15px] font-semibold tracking-tightish">
            Registrar ausencia / viaje
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={mutation.isPending}
            aria-label="Cerrar"
            className="h-8 w-8 inline-flex items-center justify-center rounded-sm border border-line-strong bg-card text-foreground hover:border-foreground transition-colors"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </header>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit) mutation.mutate();
          }}
        >
          <div className="px-6 py-5 flex flex-col gap-4">
            <p className="text-[12px] text-muted-foreground leading-relaxed">
              Se registra <strong>ya aprobada</strong> en nombre del empleado y se le notifica. El
              viaje de trabajo no consume saldo; las vacaciones sí (se permite saldo negativo).
            </p>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="reg-type">
                Tipo <span className="text-destructive">*</span>
              </Label>
              <select
                id="reg-type"
                required
                value={absenceTypeId}
                onChange={(e) => setAbsenceTypeId(e.target.value)}
                className="h-9 rounded-sm border border-line-strong bg-card px-3 text-sm text-foreground focus:border-foreground focus:outline-none"
              >
                <option value="" disabled>
                  {types.isLoading ? 'Cargando…' : 'Selecciona un tipo'}
                </option>
                {(types.data ?? []).map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="reg-start">
                  Desde <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="reg-start"
                  type="date"
                  required
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="reg-end">
                  Hasta <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="reg-end"
                  type="date"
                  required
                  disabled={isHalfDay}
                  value={effectiveEnd}
                  min={startDate || undefined}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                id="reg-half"
                type="checkbox"
                checked={isHalfDay}
                onChange={(e) => setIsHalfDay(e.target.checked)}
                className="h-4 w-4"
              />
              <Label htmlFor="reg-half" className="cursor-pointer">
                Medio día
              </Label>
              {isHalfDay ? (
                <select
                  aria-label="Mañana o tarde"
                  value={halfDayPart}
                  onChange={(e) => setHalfDayPart(e.target.value as 'AM' | 'PM')}
                  className="ml-2 h-8 rounded-sm border border-line-strong bg-card px-2 text-sm text-foreground focus:border-foreground focus:outline-none"
                >
                  <option value="AM">Mañana</option>
                  <option value="PM">Tarde</option>
                </select>
              ) : null}
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="reg-comment">Comentario</Label>
              <Textarea
                id="reg-comment"
                rows={2}
                maxLength={500}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Opcional"
              />
            </div>

            {error ? (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            ) : null}
          </div>

          <footer className="flex justify-end gap-2 px-6 py-3.5 border-t border-border bg-paper">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onClose}
              disabled={mutation.isPending}
            >
              Cancelar
            </Button>
            <Button type="submit" variant="primary" size="sm" disabled={!canSubmit}>
              {mutation.isPending ? 'Registrando…' : 'Registrar'}
            </Button>
          </footer>
        </form>
      </div>
    </div>
  );
}

function AdjustModal({
  userId,
  balance,
  onClose,
  onSaved,
}: {
  userId: string;
  balance: AbsenceBalance;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [days, setDays] = useState('');
  const [notes, setNotes] = useState('');

  const mutation = useMutation({
    mutationFn: () =>
      api<unknown>(`/admin/users/${userId}/absence-adjustments`, {
        method: 'POST',
        body: JSON.stringify({
          absenceTypeId: balance.absenceTypeId,
          days: Number(days),
          notes: notes.trim(),
        }),
      }),
    onSuccess: onSaved,
  });

  const parsedDays = Number(days);
  const canSubmit =
    days !== '' &&
    !Number.isNaN(parsedDays) &&
    parsedDays !== 0 &&
    parsedDays >= -30 &&
    parsedDays <= 30 &&
    notes.trim().length >= 3 &&
    !mutation.isPending;

  const error = mutation.error
    ? mutation.error instanceof ApiError
      ? mutation.error.message
      : 'Error al guardar el ajuste'
    : null;

  const newBalance =
    days === '' || Number.isNaN(parsedDays)
      ? balance.availableDays
      : balance.availableDays + parsedDays;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      <div
        aria-hidden
        onClick={onClose}
        className="absolute inset-0"
        style={{ background: 'rgba(10,14,31,0.4)', backdropFilter: 'blur(2px)' }}
      />
      <div className="relative w-full max-w-[480px] bg-card rounded-sm shadow-lg overflow-hidden">
        <header className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="text-[15px] font-semibold tracking-tightish">
            Ajustar saldo: {balance.name}
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={mutation.isPending}
            aria-label="Cerrar"
            className="h-8 w-8 inline-flex items-center justify-center rounded-sm border border-line-strong bg-card text-foreground hover:border-foreground transition-colors"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </header>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit) mutation.mutate();
          }}
        >
          <div className="px-6 py-5 flex flex-col gap-4">
            <div className="rounded-sm border border-border bg-paper px-4 py-3 text-[13px]">
              <p>
                Saldo actual:{' '}
                <span className="font-mono font-semibold tabular-nums">
                  {formatDays(balance.availableDays)}
                </span>
              </p>
              {days !== '' && !Number.isNaN(parsedDays) ? (
                <p className="text-[12px] text-muted-foreground mt-1">
                  Saldo después del ajuste:{' '}
                  <span
                    className={cn(
                      'font-mono font-semibold tabular-nums',
                      newBalance < 0 && 'text-destructive',
                    )}
                  >
                    {formatDays(newBalance)}
                  </span>
                </p>
              ) : null}
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="adj-days">
                Días a aplicar <span className="text-destructive">*</span>
              </Label>
              <Input
                id="adj-days"
                type="number"
                step="0.5"
                min={-30}
                max={30}
                required
                value={days}
                onChange={(e) => setDays(e.target.value)}
                placeholder="Ej. 2 o -1.5"
              />
              <p className="text-[11px] text-muted-foreground">
                Positivo suma, negativo resta. Permite medios días (paso 0.5).
              </p>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="adj-notes">
                Motivo <span className="text-destructive">*</span>
              </Label>
              <Textarea
                id="adj-notes"
                rows={3}
                required
                minLength={3}
                maxLength={500}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Ej. Horas a compensar del proyecto X"
              />
            </div>

            {error ? (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            ) : null}
          </div>

          <footer className="flex justify-end gap-2 px-6 py-3.5 border-t border-border bg-paper">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onClose}
              disabled={mutation.isPending}
            >
              Cancelar
            </Button>
            <Button type="submit" variant="primary" size="sm" disabled={!canSubmit}>
              {mutation.isPending ? 'Aplicando…' : 'Aplicar ajuste'}
            </Button>
          </footer>
        </form>
      </div>
    </div>
  );
}

function MovementsHistory({ userId, absenceTypeId }: { userId: string; absenceTypeId: string }) {
  const movements = useQuery({
    queryKey: ['admin', 'users', userId, 'absence-balances', absenceTypeId, 'movements'],
    queryFn: () =>
      api<MovementsResponse>(`/admin/users/${userId}/absence-balances/${absenceTypeId}/movements`),
  });

  if (movements.isLoading) {
    return (
      <div className="px-7 py-3 bg-paper text-[12px] text-muted-foreground">
        Cargando histórico…
      </div>
    );
  }
  if (movements.error) {
    return (
      <div className="px-7 py-3 bg-paper text-[12px] text-destructive">
        {movements.error instanceof ApiError ? movements.error.message : 'Error cargando histórico'}
      </div>
    );
  }
  const items = movements.data?.items ?? [];

  if (items.length === 0) {
    return (
      <div className="px-7 py-3 bg-paper text-[12px] text-muted-foreground">
        Sin movimientos en el ledger.
      </div>
    );
  }

  return (
    <div className="bg-paper px-7 py-3">
      <table className="w-full text-[12px]">
        <thead>
          <tr>
            <th
              className="text-left px-2 py-1.5 font-mono text-[10px] font-medium uppercase text-muted-foreground"
              style={{ letterSpacing: '0.1em' }}
            >
              Fecha
            </th>
            <th
              className="text-left px-2 py-1.5 font-mono text-[10px] font-medium uppercase text-muted-foreground"
              style={{ letterSpacing: '0.1em' }}
            >
              Tipo
            </th>
            <th
              className="text-right px-2 py-1.5 font-mono text-[10px] font-medium uppercase text-muted-foreground"
              style={{ letterSpacing: '0.1em' }}
            >
              Días
            </th>
            <th
              className="text-left px-2 py-1.5 font-mono text-[10px] font-medium uppercase text-muted-foreground"
              style={{ letterSpacing: '0.1em' }}
            >
              Origen / Notas
            </th>
          </tr>
        </thead>
        <tbody>
          {items.map((m) => (
            <tr key={m.id} className="border-t border-border/40 align-top">
              <td className="px-2 py-2 font-mono tabular-nums whitespace-nowrap">
                {m.effectiveDate}
              </td>
              <td className="px-2 py-2">
                <Badge tone={MOVEMENT_TONE[m.movementType]}>{MOVEMENT_LABEL[m.movementType]}</Badge>
              </td>
              <td
                className={cn(
                  'px-2 py-2 text-right font-mono tabular-nums font-medium',
                  m.days > 0 && 'text-success',
                  m.days < 0 && 'text-destructive',
                )}
              >
                {formatDaysSigned(m.days)}
              </td>
              <td className="px-2 py-2 text-muted-foreground">
                {m.absenceRequest ? (
                  <div>
                    Solicitud {m.absenceRequest.startDate}
                    {m.absenceRequest.endDate !== m.absenceRequest.startDate
                      ? ` → ${m.absenceRequest.endDate}`
                      : ''}{' '}
                    <span className="font-mono text-[10px] uppercase">
                      ({m.absenceRequest.status})
                    </span>
                  </div>
                ) : null}
                {m.notes ? <div>{m.notes}</div> : null}
                {m.createdByUser ? (
                  <div className="text-[10px]">por {m.createdByUser.fullName}</div>
                ) : null}
                {!m.absenceRequest && !m.notes && !m.createdByUser ? '—' : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function formatDays(d: number): string {
  if (d === 0) return '0 días';
  const isInt = Math.abs(d - Math.round(d)) < 0.001;
  const formatted = isInt ? Math.round(d).toString() : d.toFixed(1);
  return `${formatted} ${Math.abs(d) === 1 ? 'día' : 'días'}`;
}

function formatDaysSigned(d: number): string {
  if (d === 0) return '0';
  const isInt = Math.abs(d - Math.round(d)) < 0.001;
  const abs = Math.abs(d);
  const formatted = isInt ? Math.round(abs).toString() : abs.toFixed(1);
  const sign = d > 0 ? '+' : '−';
  return `${sign}${formatted}`;
}
